import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type { DocumentEmbeddingProgress } from '../../../types/knowledge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';

type UseDocumentEmbeddingStreamsOptions = {
  databaseId: string;
  documentIds: string[];
  enabled: boolean;
};

export function useDocumentEmbeddingStreams({
  databaseId,
  documentIds,
  enabled,
}: UseDocumentEmbeddingStreamsOptions) {
  const queryClient = useQueryClient();
  const [embeddingProgressByDocumentId, setEmbeddingProgressByDocumentId] =
    useState<Map<string, DocumentEmbeddingProgress>>(() => new Map());

  const streamDocumentIdsKey = useMemo(
    () => [...documentIds].sort().join(','),
    [documentIds],
  );

  useEffect(() => {
    if (!enabled || streamDocumentIdsKey === '') {
      return;
    }

    const activeDocumentIds = streamDocumentIdsKey.split(',');
    const abortControllers = activeDocumentIds.map(() => new AbortController());
    let isDisposed = false;

    const handleComplete = () => {
      if (isDisposed) {
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.documentsRoot(databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
    };

    const handleUpdate = (
      documentId: string,
      progress: DocumentEmbeddingProgress,
    ) => {
      if (isDisposed) {
        return;
      }

      setEmbeddingProgressByDocumentId((current) => {
        const next = new Map(current);
        next.set(documentId, progress);
        return next;
      });
    };

    for (const [index, documentId] of activeDocumentIds.entries()) {
      const signal = abortControllers[index]?.signal;
      if (!signal) {
        continue;
      }

      void knowledgeService
        .streamDocumentEmbeddingProgress(
          databaseId,
          documentId,
          {
            onUpdate: (progress) => handleUpdate(documentId, progress),
            onComplete: () => handleComplete(),
          },
          signal,
        )
        .catch(() => {
          // Stream failed; manual refresh remains the fallback.
        });
    }

    return () => {
      isDisposed = true;
      for (const controller of abortControllers) {
        controller.abort();
      }
    };
  }, [databaseId, enabled, queryClient, streamDocumentIdsKey]);

  return { embeddingProgressByDocumentId };
}
